import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  WalteroButton,
  WalteroCard,
  WalteroTypography
} from './waltero';
import billingService from '../services/billingService';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Calculate as CalculateIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

/**
 * Update Quantity Modal
 *
 * Handles subscription quantity changes (increase/decrease)
 * Shows proration preview for increases
 */
const UpdateQuantityModal = ({
  open,
  onClose,
  subscription,
  organizationId,
  mode = 'increase', // 'increase' or 'decrease'
  onSuccess
}) => {
  const theme = useTheme();
  const [newQuantity, setNewQuantity] = useState(subscription?.purchasedSensorQuantity || 100);
  const [prorationPreview, setProrationPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Set initial quantity when subscription changes
  useEffect(() => {
    if (subscription) {
      setNewQuantity(
        mode === 'increase'
          ? subscription.purchasedSensorQuantity + 10
          : Math.max(subscription.assignedSensorCount, subscription.purchasedSensorQuantity - 10)
      );
    }
  }, [subscription, mode]);

  // Load proration preview for increases
  useEffect(() => {
    if (open && subscription && mode === 'increase' && newQuantity > subscription.purchasedSensorQuantity) {
      loadProrationPreview();
    }
  }, [open, newQuantity, subscription, mode]);

  const loadProrationPreview = async () => {
    if (!subscription || newQuantity === subscription.purchasedSensorQuantity) {
      setProrationPreview(null);
      return;
    }

    try {
      setLoadingPreview(true);
      const preview = await billingService.previewQuantityChange(
        organizationId,
        subscription.id,
        newQuantity
      );
      setProrationPreview(preview);
    } catch (err) {
      console.error('Error loading proration preview:', err);
      // Don't show error for preview failure
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle quantity change
  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value) || 0;

    if (mode === 'decrease') {
      // Can't decrease below assigned sensors
      if (value >= subscription.assignedSensorCount && value < subscription.purchasedSensorQuantity) {
        setNewQuantity(value);
      }
    } else {
      // Increase must be greater than current
      if (value > subscription.purchasedSensorQuantity && value <= 10000) {
        setNewQuantity(value);
      }
    }
  };

  // Quick quantity adjustments
  const quickAdjustments = mode === 'increase'
    ? [10, 25, 50, 100, 250]
    : [-10, -25, -50, -100];

  // Apply quick adjustment
  const applyQuickAdjustment = (adjustment) => {
    const adjusted = subscription.purchasedSensorQuantity + adjustment;

    if (mode === 'decrease') {
      setNewQuantity(Math.max(subscription.assignedSensorCount, adjusted));
    } else {
      setNewQuantity(Math.min(10000, adjusted));
    }
  };

  // Validate quantity
  const isValidQuantity = () => {
    if (mode === 'decrease') {
      return newQuantity >= subscription.assignedSensorCount &&
             newQuantity < subscription.purchasedSensorQuantity;
    } else {
      return newQuantity > subscription.purchasedSensorQuantity &&
             newQuantity <= 10000;
    }
  };

  // Handle update
  const handleUpdate = async () => {
    if (!isValidQuantity()) {
      setError('Invalid quantity selected');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      if (mode === 'increase') {
        await billingService.increaseSubscriptionQuantity(
          organizationId,
          subscription.id,
          newQuantity
        );
      } else {
        await billingService.decreaseSubscriptionQuantity(
          organizationId,
          subscription.id,
          newQuantity
        );
      }
      onSuccess();
    } catch (err) {
      console.error('Error updating quantity:', err);
      setError(err.error || 'Failed to update quantity');
    } finally {
      setProcessing(false);
    }
  };

  // Calculate change details
  const calculateChange = () => {
    if (!subscription) return {};

    const difference = newQuantity - subscription.purchasedSensorQuantity;
    const monthsRemaining = subscription.currentPeriodEnd
      ? Math.max(0, Math.ceil(
          (new Date(subscription.currentPeriodEnd) - new Date()) / (30 * 24 * 60 * 60 * 1000)
        ))
      : 12;

    const pricePerSensor = 1; // $1 per sensor per month
    const immediateCharge = mode === 'increase'
      ? Math.abs(difference) * pricePerSensor * monthsRemaining
      : 0;

    const newAnnualCost = newQuantity * pricePerSensor * 12;
    const currentAnnualCost = subscription.purchasedSensorQuantity * pricePerSensor * 12;

    return {
      difference,
      monthsRemaining,
      immediateCharge,
      newAnnualCost,
      currentAnnualCost,
      annualSavings: currentAnnualCost - newAnnualCost
    };
  };

  const changeDetails = calculateChange();

  if (!subscription) return null;

  return (
    <Dialog
      open={open}
      onClose={() => !processing && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          {mode === 'increase' ? (
            <TrendingUpIcon sx={{ fontSize: 32, color: theme.palette.success.main, mr: 2 }} />
          ) : (
            <TrendingDownIcon sx={{ fontSize: 32, color: theme.palette.warning.main, mr: 2 }} />
          )}
          <Box>
            <WalteroTypography variant="h5" fontWeight="bold">
              {mode === 'increase' ? 'Increase' : 'Decrease'} Sensor Quantity
            </WalteroTypography>
            <Typography variant="body2" color="textSecondary">
              {subscription.subscriptionTypeName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Current Configuration */}
        <WalteroCard sx={{ p: 2, mb: 3, backgroundColor: theme.palette.grey[50] }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Current Quantity
              </Typography>
              <Typography variant="h6">
                {subscription.purchasedSensorQuantity} sensors
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Currently Assigned
              </Typography>
              <Typography variant="h6">
                {subscription.assignedSensorCount} sensors
              </Typography>
            </Grid>
          </Grid>
        </WalteroCard>

        {/* New Quantity Selection */}
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            New Sensor Quantity
          </Typography>

          {/* Quick Adjustments */}
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {quickAdjustments.map((adj) => (
              <Chip
                key={adj}
                label={adj > 0 ? `+${adj}` : adj}
                onClick={() => applyQuickAdjustment(adj)}
                color={mode === 'increase' ? 'success' : 'warning'}
                variant="outlined"
              />
            ))}
          </Box>

          {/* Quantity Input */}
          <TextField
            fullWidth
            type="number"
            label="New Quantity"
            value={newQuantity}
            onChange={handleQuantityChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalculateIcon />
                </InputAdornment>
              ),
              inputProps: {
                min: mode === 'decrease' ? subscription.assignedSensorCount : subscription.purchasedSensorQuantity + 1,
                max: mode === 'decrease' ? subscription.purchasedSensorQuantity - 1 : 10000
              }
            }}
            helperText={
              mode === 'decrease'
                ? `Minimum: ${subscription.assignedSensorCount} (currently assigned)`
                : `Must be greater than current quantity (${subscription.purchasedSensorQuantity})`
            }
            error={!isValidQuantity() && newQuantity !== subscription.purchasedSensorQuantity}
          />
        </Box>

        {/* Change Summary */}
        {isValidQuantity() && (
          <WalteroCard
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: mode === 'increase'
                ? theme.palette.success.light + '20'
                : theme.palette.warning.light + '20',
              border: `1px solid ${mode === 'increase' ? theme.palette.success.main : theme.palette.warning.main}`
            }}
          >
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Change Summary
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="textSecondary">
                    Quantity Change
                  </Typography>
                  <Typography variant="h6" color={mode === 'increase' ? 'success.main' : 'warning.main'}>
                    {changeDetails.difference > 0 ? '+' : ''}{changeDetails.difference} sensors
                  </Typography>
                </Box>
              </Grid>

              {mode === 'increase' && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="textSecondary">
                        Immediate Charge
                      </Typography>
                      <Typography variant="h5" color="primary">
                        ${changeDetails.immediateCharge?.toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {Math.abs(changeDetails.difference)} sensors × $1/month × {changeDetails.monthsRemaining} months remaining
                    </Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Current Annual Cost
                </Typography>
                <Typography variant="body1">
                  ${changeDetails.currentAnnualCost?.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  New Annual Cost
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  ${changeDetails.newAnnualCost?.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </WalteroCard>
        )}

        {/* Proration Details (for increases) */}
        {mode === 'increase' && prorationPreview && !loadingPreview && (
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              You'll be charged ${changeDetails.immediateCharge?.toFixed(2)} today for the additional sensors.
              Your next annual renewal will be ${changeDetails.newAnnualCost?.toFixed(2)}.
            </Typography>
          </Alert>
        )}

        {/* Schedule Notice (for decreases) */}
        {mode === 'decrease' && isValidQuantity() && (
          <Alert severity="warning" icon={<ScheduleIcon />}>
            <Typography variant="body2">
              <strong>Important:</strong> The quantity decrease will be scheduled to take effect at your next
              billing period ({subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'next renewal'}).
              No refund will be issued for the current period. You can cancel this scheduled change anytime before it takes effect.
            </Typography>
          </Alert>
        )}

        {/* Validation Error for Decrease */}
        {mode === 'decrease' && newQuantity < subscription.assignedSensorCount && (
          <Alert severity="error">
            Cannot decrease below {subscription.assignedSensorCount} sensors as they are currently assigned to devices.
            Please unassign sensors first.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <WalteroButton
          onClick={onClose}
          disabled={processing}
        >
          CANCEL
        </WalteroButton>
        <WalteroButton
          variant="contained"
          onClick={handleUpdate}
          disabled={processing || !isValidQuantity()}
          color={mode === 'increase' ? 'success' : 'warning'}
          startIcon={processing ? <CircularProgress size={16} /> : (
            mode === 'increase' ? <TrendingUpIcon /> : <TrendingDownIcon />
          )}
        >
          {processing
            ? 'UPDATING...'
            : mode === 'increase'
              ? `INCREASE & PAY $${changeDetails.immediateCharge?.toFixed(2)}`
              : 'SCHEDULE DECREASE'}
        </WalteroButton>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateQuantityModal;